/**
 * Staff Reports Page (Refactored)
 * Uses modular tab components for better maintainability
 * Tab state is derived directly from URL - single source of truth
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Users, Moon, AlertTriangle, Baby } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";

// Import modular tab components
// Note: FireDrillLogTab, LiabilityInsuranceTab, OccupancyPermitTab, TransportationNotificationTab, EmergencyDrillLogTab moved to Compliance Dashboard
// Note: StaffEvaluationTab moved to Staff Records (per-staff clearances)
import {
  StaffAssignmentTab,
  SleepingCotsTab,
  IncidentReportTab,
  InfantDailyReportTab,
  ToddlerDailyReportTab,
  InfantCribCardTab,
  StaffEvaluationTab
} from "@/components/staff-reports";
import PrintReportHeader from "@/components/common/PrintReportHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIMARY_OWNER_EMAIL = "todayskidz@comcast.net";

const StaffReportsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [children, setChildren] = useState([]);
  const [staffRecords, setStaffRecords] = useState([]);
  const [permissions, setPermissions] = useState({});
  
  // SINGLE SOURCE OF TRUTH: Derive activeTab directly from URL on every render
  // Default to "sleeping_cots" when no tab param is present
  const activeTab = searchParams.get('tab') || "sleeping_cots";

  const isPrimaryOwner = user?.email?.toLowerCase() === PRIMARY_OWNER_EMAIL.toLowerCase() || user?.isPrimaryOwner;
  const centerName = organization?.name || "Childcare Center";

  // Handle tab changes by updating URL (which will re-render with new activeTab)
  const handleTabChange = (newTab) => {
    setSearchParams({ tab: newTab });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, childrenRes, staffRecordsRes, permissionsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/children`),
        axios.get(`${API}/staff-records`).catch(() => ({ data: { records: [] } })),
        axios.get(`${API}/owner/permissions`).catch(() => ({ data: { permissions: {} } }))
      ]);

      setStaff(usersRes.data || []);
      setChildren((childrenRes.data || []).filter(c => c.active !== false));
      setStaffRecords(staffRecordsRes.data?.records || []);
      setPermissions(permissionsRes.data?.permissions || {});
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppShell pageTitle="Loading..." breadcrumb>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-ink-muted font-medium">Loading reports...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  // Determine page title based on active tab
  const pageTitle = activeTab === "staff_assignment" ? "Daily Roster" : activeTab === "staff_evaluation" ? "Staff Evaluation" : "Daily Reports";
  // Hide tabs when on Staff Assignment or Staff Evaluation (accessed from Staff sidebar section)
  const showTabs = activeTab !== "staff_assignment" && activeTab !== "staff_evaluation";

  return (
    <AppShell pageTitle={pageTitle} breadcrumb>
      {/* Hero Banner - Show staff assignment hero for that tab */}
      {activeTab === "staff_assignment" && (
        <PageHero 
          imageUrl={HERO_IMAGES.staffAssignment}
          title="Daily Roster"
          subtitle="Update your daily roster assignments"
          altText="Teachers in their classrooms with children"
        />
      )}
      
      <div className="relative z-10 print:bg-white">
        {/* Print Header - Only visible when printing */}
        <PrintReportHeader reportTitle="Staff Reports" />
        
        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          {/* Tab Navigation - Two Rows for better organization - Hidden for Staff Assignment */}
          {showTabs && (
          <div className="space-y-2 print:hidden">
            {/* Row 1: Daily Reports */}
            <TabsList className="grid grid-cols-2 lg:grid-cols-4 gap-2 bg-white p-2 rounded-xl shadow-card border border-line">
              <TabsTrigger
                value="sleeping_cots"
                className="data-[state=active]:bg-brand-secondary data-[state=active]:text-white flex items-center gap-2"
              >
                <Moon className="w-4 h-4" />
                <span className="hidden sm:inline">Sleeping Cots</span>
              </TabsTrigger>
              <TabsTrigger
                value="incident_report"
                className="data-[state=active]:bg-brand-accent data-[state=active]:text-white flex items-center gap-2"
              >
                <AlertTriangle className="w-4 h-4" />
                <span className="hidden sm:inline">Incident Report</span>
              </TabsTrigger>
            </TabsList>

            {/* Row 2: Infant/Toddler Reports */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-pink-700 px-2">Infant/Toddler:</span>
              <TabsList className="grid grid-cols-3 gap-2 bg-gradient-to-r from-pink-50 to-purple-50 p-2 rounded-xl flex-1 max-w-xl">
                <TabsTrigger
                  value="infant_daily"
                  className="data-[state=active]:bg-pink-500 data-[state=active]:text-white flex items-center gap-2"
                  data-testid="infant-daily-tab"
                >
                  <Baby className="w-4 h-4" />
                  <span className="hidden sm:inline">Infant Report</span>
                </TabsTrigger>
                <TabsTrigger
                  value="toddler_daily"
                  className="data-[state=active]:bg-purple-500 data-[state=active]:text-white flex items-center gap-2"
                  data-testid="toddler-daily-tab"
                >
                  <Baby className="w-4 h-4" />
                  <span className="hidden sm:inline">Toddler Report</span>
                </TabsTrigger>
                <TabsTrigger
                  value="infant_crib_card"
                  className="data-[state=active]:bg-blue-500 data-[state=active]:text-white flex items-center gap-2"
                  data-testid="infant-crib-card-tab"
                >
                  <Baby className="w-4 h-4" />
                  <span className="hidden sm:inline">Crib Card</span>
                </TabsTrigger>
              </TabsList>
            </div>
          </div>
          )}

          {/* Tab Contents */}
          <TabsContent value="sleeping_cots">
            <SleepingCotsTab children={children} centerName={centerName} />
          </TabsContent>

          <TabsContent value="incident_report">
            <IncidentReportTab children={children} centerName={centerName} />
          </TabsContent>

          {/* Infant/Toddler Report Tab Contents */}
          <TabsContent value="infant_daily">
            <InfantDailyReportTab children={children} centerName={centerName} />
          </TabsContent>

          <TabsContent value="toddler_daily">
            <ToddlerDailyReportTab children={children} centerName={centerName} />
          </TabsContent>

          <TabsContent value="infant_crib_card">
            <InfantCribCardTab children={children} centerName={centerName} staff={staff} />
          </TabsContent>
          
          {/* Staff Assignment - Accessed via Staff sidebar, not shown in Daily Reports tabs */}
          <TabsContent value="staff_assignment">
            <StaffAssignmentTab
              staff={staff}
              children={children}
              centerName={centerName}
              currentUser={user}
              canViewAllStaff={
                user?.role === 'owner'
                  ? true
                  : (permissions['roster_view_all_staff']
                      ? permissions['roster_view_all_staff'][user?.role] !== false
                      : true)
              }
            />
          </TabsContent>

          {/* Staff Evaluation - Owner only, accessed via Staff sidebar */}
          <TabsContent value="staff_evaluation">
            <StaffEvaluationTab
              staffRecords={staffRecords}
              staff={staff}
              isOwner={isPrimaryOwner}
              currentUserEmail={user?.email}
              centerName={centerName}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
};

export default StaffReportsPage;
